/**
 * Runtime validation of required client-side environment variables.
 *
 * Single source of truth for which `VITE_*` vars the app needs to boot.
 * Used by `integrations/supabase/client.ts` to gate the supabase client
 * and by `main.tsx` to render the MaintenanceScreen with actionable detail
 * in dev builds.
 *
 * Why a separate module: the supabase client is imported from many places,
 * so its module-load side-effect (the `console.warn`) used to be the only
 * signal a misconfigured env produced. That fires once and is easy to miss
 * in the noise of dev startup. This module exposes a typed `validateEnv()`
 * the maintenance screen can render, so a missing var is a *visible*
 * failure mode, not a buried log line.
 */

type EnvSource = Record<string, string | undefined>;

function readEnv(): EnvSource {
  // Browser/Vite path
  if (typeof window !== 'undefined') {
    return ((import.meta as any).env ?? {}) as EnvSource;
  }
  // Node path (SSR / dev-server middleware / build scripts)
  if (typeof process !== 'undefined' && process.env) {
    return process.env as EnvSource;
  }
  return {};
}

/**
 * Vars without which the app cannot reach its backend at all.
 * Missing any of these means rendering the MaintenanceScreen instead of <App/>.
 */
export const REQUIRED_ENV_VARS = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_PUBLISHABLE_KEY',
] as const;

/**
 * Vars used by individual features. Missing values silently disable the
 * feature rather than blocking boot — but we still surface them in dev
 * so a developer touching that feature isn't left guessing why nothing
 * happens.
 */
export const OPTIONAL_ENV_VARS = [
  'VITE_SUPABASE_PROJECT_ID',     // edge-function callers (calendar, push, device-auth)
  'VITE_SUPABASE_ANON_KEY',       // legacy fallback alongside PUBLISHABLE_KEY
  'VITE_POSTHOG_KEY',             // analytics
  'VITE_POSTHOG_HOST',            // analytics endpoint override
  'VITE_REQUIRE_AUTH_IN_DEV',     // dev auth toggle
] as const;

export interface EnvValidationResult {
  ok: boolean;
  missingRequired: string[];
  missingOptional: string[];
}

export function validateEnv(): EnvValidationResult {
  const env = readEnv();
  const missingRequired = REQUIRED_ENV_VARS.filter((k) => !env[k]);
  const missingOptional = OPTIONAL_ENV_VARS.filter((k) => !env[k]);
  return {
    ok: missingRequired.length === 0,
    missingRequired,
    missingOptional,
  };
}
